import { Link, useNavigate } from "react-router-dom";
import { Drawer } from "rsuite";
import { useSettings } from "../../context/SettingsContext";
import "rsuite/dist/rsuite.min.css";
import { useSelector, useDispatch } from "react-redux";
import { sidebarAction } from "../../store/reducers/sidebarSlice";
import logo from "../../assets/icon/logo-light.svg";
import { Disclosure } from "@headlessui/react";
import { ChevronRightIcon } from "@heroicons/react/24/solid";
import useCategory from "../../apiHooks/useCategory";
import imgUrl from "../../imgUrl";

const SideBar = () => {
  const { logoUrlLight } = useSettings();
  const open = useSelector((state) => state.sidebar.value);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: categories = [], isLoading } = useCategory();

  // Build parent → children map
  const parentCategories = categories.filter((c) => !c.parent_id);
  const childMap = categories.reduce((acc, c) => {
    if (c.parent_id) {
      acc[c.parent_id] = acc[c.parent_id] || [];
      acc[c.parent_id].push(c);
    }
    return acc;
  }, {});

  const isHierarchical = categories.some((c) => c.parent_id);
  const displayCategories = isHierarchical ? parentCategories : categories;

  const handleCategoryClick = (id) => {
    navigate(`/search?category_id=${id}`);
    dispatch(sidebarAction(false));
  };

  const skeletons = Array.from({ length: 8 });

  return (
    <Drawer
      size={"xs"}
      placement="left"
      open={open}
      onClose={() => dispatch(sidebarAction(false))}
    >
      <div className="drawer-mask"></div>
      <div className="flex flex-col w-full h-full bg-white cursor-pointer scrollbar-hide">
        {/* Header */}
        <div className="w-full flex justify-between items-center h-16 px-6 py-4 bg-emerald-500 text-white border-b border-gray-100">
          <h2 className="font-semibold text-lg m-0 text-heading flex items-center">
            <Link className="mr-10 !no-underline" to="/" onClick={() => dispatch(sidebarAction(false))}>
              <img className="h-9 w-24 object-contain" src={logoUrlLight} alt="Logo" />
            </Link>
          </h2>
          <button
            onClick={() => dispatch(sidebarAction(false))}
            className="flex text-xl items-center justify-center w-8 h-8 rounded-full bg-gray-50 text-red-500 p-2 focus:outline-none transition-opacity hover:text-red-600"
          >
            <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
              <path d="M289.94 256l95-95A24 24 0 00351 127l-95 95-95-95a24 24 0 00-34 34l95 95-95 95a24 24 0 1034 34l95-95 95 95a24 24 0 0034-34z"></path>
            </svg>
          </button>
        </div>

        <div className="overflow-y-scroll scrollbar-hide w-full max-h-full">
          <h2 className="font-semibold text-lg m-0 text-heading flex align-center border-b px-8 py-3">
            All Categories
          </h2>
          <div className="relative grid gap-2 p-6">
            {isLoading
              ? skeletons.map((_, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 animate-pulse">
                    <div className="w-5 h-5 bg-gray-200 rounded" />
                    <div className="h-3 bg-gray-200 rounded flex-1" />
                  </div>
                ))
              : displayCategories.map((category) => {
                  const children = childMap[category.id] || category.sub_categories || [];
                  return (
                    <div key={category.id}>
                      <Disclosure>
                        {({ open: disclosureOpen }) => (
                          <>
                            {children.length > 0 ? (
                              /* Has children — use Disclosure toggle */
                              <Disclosure.Button className="p-2 flex items-center rounded-md hover:bg-gray-50 w-full hover:text-emerald-600 group">
                                {/* Icon */}
                                <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                                  {category.image ? (
                                    <img
                                      src={`${imgUrl}/${category.image}`}
                                      alt={category.name}
                                      className="w-5 h-5 object-contain"
                                      onError={(e) => { e.target.style.display = "none"; }}
                                    />
                                  ) : (
                                    <svg className="w-4 h-4 text-gray-400 group-hover:text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                  )}
                                </div>
                                <div className="inline-flex items-center justify-between ml-3 text-sm font-medium w-full hover:text-emerald-600">
                                  {category.name}
                                </div>
                                <ChevronRightIcon
                                  className={`${disclosureOpen ? "rotate-90 transform" : ""} h-4 w-4 transition duration-200 ease-in-out text-gray-400 flex-shrink-0`}
                                />
                              </Disclosure.Button>
                            ) : (
                              /* No children — direct click */
                              <button
                                onClick={() => handleCategoryClick(category.id)}
                                className="p-2 flex items-center rounded-md hover:bg-gray-50 w-full hover:text-emerald-600 group"
                              >
                                <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                                  {category.image ? (
                                    <img
                                      src={`${imgUrl}/${category.image}`}
                                      alt={category.name}
                                      className="w-5 h-5 object-contain"
                                      onError={(e) => { e.target.style.display = "none"; }}
                                    />
                                  ) : (
                                    <svg className="w-4 h-4 text-gray-400 group-hover:text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                  )}
                                </div>
                                <div className="inline-flex items-center justify-between ml-3 text-sm font-medium w-full">
                                  {category.name}
                                </div>
                              </button>
                            )}

                            {/* Parent category as a direct link when expanded */}
                            {children.length > 0 && (
                              <Disclosure.Panel>
                                <ul className="pl-8 pb-3 pt-1 -mt-1 space-y-0.5">
                                  {/* "All in [category]" option */}
                                  <li>
                                    <button
                                      onClick={() => handleCategoryClick(category.id)}
                                      className="flex items-center py-1.5 text-sm text-emerald-600 font-medium hover:text-emerald-700 w-full text-left"
                                    >
                                      <span className="text-xs mr-1">—</span>
                                      All {category.name}
                                    </button>
                                  </li>
                                  {children.map((sub) => (
                                    <li key={sub.id}>
                                      <button
                                        onClick={() => handleCategoryClick(sub.id)}
                                        className="flex items-center py-1.5 text-sm text-gray-600 hover:text-emerald-600 w-full text-left transition-colors"
                                      >
                                        <svg className="w-3 h-3 mr-2 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                        {sub.name}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </Disclosure.Panel>
                            )}
                          </>
                        )}
                      </Disclosure>
                    </div>
                  );
                })}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default SideBar;
